// import components
import Chip from '@mui/material/Chip';
import VirtualizedAutocomplete from 'components/shared/Autocomplete/VirtualizedAutocomplete';

// import hooks
import { usePlanUnitBranch } from '../../PlanUnitSelector.hooks';

// import utils
import classNames from 'classnames';
import { isValidPlanUnit } from '../../util';

// import types
import type { FC } from 'react';
import type { PlanUnitMix } from 'components/types';
import type { PlanUnitSelectorMultiProps } from '../../PlanUnitSelector.types';

// import styles
import { StyledCustomTextField } from '../../PlanUnitSelector.styles';

const PlanUnitSelectorMulti: FC<PlanUnitSelectorMultiProps> = ({
  options,
  selectedOptions,
  onChange,
  compareInterval,
  isOpen,
  onOpen,
  onClose,
  search,
  showChips,
  fullWidth,
  disabled,
  error,
  helperText,
  variant,
  isToolbar,
  onClear
}) => {
  'use memo';

  const { t, getOptionLabel, filterOptions, renderOption, extraWidth } =
    usePlanUnitBranch({ compareInterval, isMulti: true });

  const renderTags = (tagOptions: PlanUnitMix[]) =>
    tagOptions.map((option) => {
      const isInvalid =
        !('costCenters' in option) &&
        !isValidPlanUnit(option.validity, compareInterval);
      return (
        <Chip
          key={option.uniqId}
          label={
            isInvalid
              ? `${option.name} (${t('invalid').toLowerCase()})`
              : option.name
          }
          variant="outlined"
          className={classNames({ invalid: isInvalid })}
          onDelete={() =>
            onChange(
              null,
              selectedOptions.filter((o) => o.uniqId !== option.uniqId)
            )
          }
        />
      );
    });

  return (
    <VirtualizedAutocomplete
      autoHighlight
      disableCloseOnSelect
      clearOnBlur={false}
      clearOnEscape={false}
      extraWidth={extraWidth}
      filterOptions={filterOptions}
      getLimitTagsText={(cnt) => `${cnt} ${t('selected').toLocaleLowerCase()}`}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(a, b) => a.uniqId === b.uniqId}
      limitTags={showChips ? undefined : 0}
      multiple
      onChange={onChange}
      onClose={onClose}
      onOpenCallback={onOpen}
      open={isOpen}
      options={options}
      size="small"
      renderInput={(params) => (
        <StyledCustomTextField
          label={t('planUnits')}
          error={error}
          helperText={helperText}
          hiddenLabel={isToolbar}
          variant={variant}
          isToolbar={isToolbar}
          onChange={(e) => {
            search.setSearchCriteria(e.target.value);
          }}
          onBlur={search.clearSearchCriteria}
          {...params}
          size="small"
        />
      )}
      renderOption={renderOption}
      renderTags={renderTags}
      value={selectedOptions}
      fullWidth={fullWidth}
      inputValue={search.inputValue}
      onInputChange={(event, value, reason) => {
        reason === 'clear' && onClear && onClear();
        search.onInputChange(event, value, reason);
      }}
      disabled={disabled}
    />
  );
};

export default PlanUnitSelectorMulti;
